import {
  productNewsListResponseSchema,
  productNewsMarkReadResponseSchema,
  productNewsUnreadCountSchema,
  type ProductNewsCategory,
  type ProductNewsListResponse,
  type ProductNewsMarkReadResponse,
  type ProductNewsUnreadCount,
  type SupportedLocale,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiFetch, type ApiError } from '@/lib/api-client';

export interface ProductNewsParams {
  locale: SupportedLocale;
  category?: ProductNewsCategory;
}

export const productNewsKeys = {
  all: ['product-news'] as const,
  list: (params: ProductNewsParams) => ['product-news', 'list', params] as const,
  unread: ['product-news', 'unread-count'] as const,
};

export function useProductNews(
  params: ProductNewsParams,
): UseQueryResult<ProductNewsListResponse, ApiError> {
  const search = new URLSearchParams({
    locale: params.locale,
    page: '1',
    pageSize: '50',
  });
  if (params.category !== undefined) search.set('category', params.category);

  return useQuery({
    queryKey: productNewsKeys.list(params),
    queryFn: () =>
      apiFetch(`/api/v1/product-news?${search.toString()}`, productNewsListResponseSchema),
  });
}

export function useProductNewsUnreadCount(
  enabled = true,
): UseQueryResult<ProductNewsUnreadCount, ApiError> {
  return useQuery({
    queryKey: productNewsKeys.unread,
    enabled,
    queryFn: () => apiFetch('/api/v1/product-news/unread-count', productNewsUnreadCountSchema),
    staleTime: 60_000,
  });
}

export function useMarkProductNewsRead(): UseMutationResult<
  ProductNewsMarkReadResponse,
  ApiError,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId) =>
      apiFetch('/api/v1/product-news/read', productNewsMarkReadResponseSchema, {
        method: 'POST',
        body: { entryId },
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(productNewsKeys.unread, { unreadCount: result.unreadCount });
      void queryClient.invalidateQueries({ queryKey: productNewsKeys.all });
    },
  });
}
